import React, { useRef } from 'react';
import { get } from 'lodash';
import { Link, useParams, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { normalizeWebsiteUrl } from '@mssgme/helpers';
import { useWorkspace, useActions, useFetching } from '@mssgme/hooks';
import { isMatchId } from '@mssgme/store';
import {
    SidebarHeader,
    ScrollableBox,
    Button,
    NotifyAndClose,
    EnhancedNavigationGuard,
    EnhancedFormik,
    Form,
    SaveButton,
    CancelButton,
    Row,
    TextField,
    CheckboxField,
    Feature,
} from '@mssgme/ui';

import styles from './LinkEdit.scss';

function useLink(linkId) {
    const { card } = useWorkspace();
    const link = linkId ? card.links.find(isMatchId(linkId)) : null;
    const ref = useRef(link);

    return ref.current;
}

export default function LinkEdit() {
    const { t } = useTranslation('cards');
    const { linkId } = useParams();
    const actions = useActions();
    const deleteLink = useFetching(actions.api.card.links.delete);
    const link = useLink(linkId);

    const isNew = !linkId;

    if (!isNew && !link) throw new Error('No link');

    const leftButton = <CancelButton tag={Link} to="/p/edit/links" styled="backIcon" />;

    return (
        <EnhancedFormik
            initialValues={{
                title: get(link, 'title', ''),
                url: get(link, 'url', ''),
                enabled: get(link, 'enabled', true),
            }}
            onSubmit={(payload) => {
                return isNew
                    ? actions.api.card.links.create({ payload })
                    : actions.api.card.links.update({ linkId, payload });
            }}
        >
            {({ values, status, setFieldValue }) => (
                <EnhancedNavigationGuard>
                    {status === 'success' && (
                        <>
                            <NotifyAndClose>{t('CardEditor.saved')}</NotifyAndClose>
                            <Redirect to="/p/edit/links" />
                        </>
                    )}
                    {deleteLink.fulfilled && (
                        <>
                            <NotifyAndClose>{t('CardEditor.removed')}</NotifyAndClose>
                            <Redirect to="/p/edit/links" />
                        </>
                    )}
                    <div className={styles.root}>
                        <SidebarHeader
                            heading={t('linkEdit.new_link')}
                            leftButton={leftButton}
                            rightButton={<SaveButton />}
                        />
                        <ScrollableBox>
                            <Feature rule={isNew ? 'links:create' : 'links:edit'} subject={link}>
                                <Form>
                                    <TextField
                                        name="title"
                                        label={t('linkEdit.title')}
                                        placeholder={t('linkEdit.title_placeholder')}
                                        maxLength={100}
                                        autoFocus
                                    />
                                    <TextField
                                        name="url"
                                        label={t('linkEdit.link')}
                                        placeholder={t('linkEdit.link_placeholder')}
                                        onBlur={() => {
                                            setFieldValue('url', normalizeWebsiteUrl(values.url));
                                        }}
                                    />
                                    <Row className={styles.groupedElements}>
                                        <CheckboxField name="enabled" label={t('linkEdit.enabled')} />
                                        {!isNew && (
                                            <div className={styles.actions}>
                                                <Button
                                                    btnStyle="danger"
                                                    size="small"
                                                    type="button"
                                                    onClick={() => deleteLink.run({ linkId })}
                                                >
                                                    {t('linkEdit.delete')}
                                                </Button>
                                            </div>
                                        )}
                                    </Row>
                                </Form>
                            </Feature>
                        </ScrollableBox>
                    </div>
                </EnhancedNavigationGuard>
            )}
        </EnhancedFormik>
    );
}
